/*
 * FleetManager — the single owner of the LIVE fleet stream + canonical fleet state,
 * mirroring the app-ui Manager pattern: it opens ONE `SubscribeFleet`
 * Connect server-stream (snapshot → ~1 Hz deltas), holds the gateways, and emits
 * `change`. NOTHING in the UI polls or fetches — views read `list()`/`get()` and
 * subscribe to the `change` event. Reconnect-forever.
 *
 * VIEWPORT-SCOPED by construction: the request carries the in-view gateways, so the
 * backend only watches what's on screen (empty = whole fleet, which is the right
 * scope while every gateway fits on screen). When the fleet grows past a screenful
 * and the UI virtualises, it calls `setViewport(...)` and the backend cost tracks
 * what's visible, not the fleet size — the path to millions of gateways.
 */
import { createClient, type Transport } from '@connectrpc/connect';
import { FleetControl } from '@vitesse/proto/vitesse/v1/fleet_control_pb.js';
import { type CloudServer, type FleetGateway, toCloudServer, toFleetGateway } from './fleet-client';

export class FleetManager extends EventTarget {
  private gateways = new Map<string, FleetGateway>();
  private cloud: CloudServer[] = []; // OUR cloud servers (full list, replaced per frame)
  private viewport: string[] = []; // in-view fingerprints; [] = whole fleet
  private ac?: AbortController;
  private started = false;
  /** true once the first snapshot has landed — the UI drops its loader on this. */
  ready = false;
  /** last stream error ('' when healthy) — surfaced so the UI can show a banner. */
  error = '';

  constructor(private readonly transport: Transport) {
    super();
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    void this.run();
  }

  stop(): void {
    this.started = false;
    this.ac?.abort();
  }

  list(): FleetGateway[] {
    return [...this.gateways.values()];
  }

  get(fp: string): FleetGateway | undefined {
    return this.gateways.get(fp);
  }

  /** OUR private cloud servers (self-locating nodes) — stats present only for a
   *  super-admin viewer (the server strips them otherwise). */
  cloudServers(): CloudServer[] {
    return this.cloud;
  }

  /** Force a fresh snapshot — e.g. after a remove/unclaim, whose deletion the
   *  delta stream won't otherwise reflect (a removed gateway simply stops appearing
   *  in deltas). Re-opens the stream → a clean snapshot of the current fleet. */
  refresh(): void {
    if (this.started) void this.run();
  }

  /** Re-scope the live stream to the visible gateways. Empty = whole fleet. No-op
   *  when unchanged. */
  setViewport(fps: string[]): void {
    const key = [...fps].sort().join(',');
    if (key === [...this.viewport].sort().join(',')) return;
    this.viewport = fps;
    if (this.started) void this.run(); // re-open with the new scope
  }

  private async run(): Promise<void> {
    this.ac?.abort();
    const ac = new AbortController();
    this.ac = ac;
    const client = createClient(FleetControl, this.transport);
    while (this.started && !ac.signal.aborted) {
      try {
        const stream = client.subscribeFleet({ deviceFingerprints: this.viewport }, { signal: ac.signal });
        for await (const update of stream) {
          if (update.isSnapshot) this.gateways.clear();
          for (const r of update.gateways) {
            const g = toFleetGateway(r);
            this.gateways.set(g.deviceFingerprint, g);
          }
          // `?? []` guards a stale @vitesse/proto (no cloud_servers field) — never crash
          // the whole stream over a missing field; it just stays empty until the proto updates.
          this.cloud = (update.cloudServers ?? []).map(toCloudServer); // full list each frame
          this.ready = true;
          this.error = '';
          this.dispatchEvent(new Event('change'));
        }
      } catch (err) {
        if (ac.signal.aborted) return; // viewport change or stop() — a new run() owns it now
        this.error = err instanceof Error ? err.message : String(err);
        this.dispatchEvent(new Event('change'));
        await new Promise((r) => setTimeout(r, 1500)); // backoff, then retry forever
      }
    }
  }
}
